// =============================================================================
// catalog-client.http.ts — Adaptateurs HTTP RÉELS des clients catalog LECTURE (S8-D2).
// =============================================================================
// Remplacent les stubs-first S8 derrière les MÊMES DI seams (CatalogAdminClientProvider,
// CatalogSupplierClientProvider) : les écrans/hooks consomment les interfaces
// CatalogAdminClient / CatalogSupplierClient, l'impl est substituée sans toucher l'UI.
//
// AUTORITÉ = API (NestJS). `catalog` est un référentiel GLOBAL, admin-only côté données,
// SANS PII : ces adaptateurs ne portent AUCUNE règle métier, ils sérialisent l'intention
// de LECTURE, parsent la réponse et mappent les erreurs.
//
// SÉCURITÉ (RULE_REF_006) : le bearer/session n'est JAMAIS fabriqué ni stocké ici. La
// session ADMIN (A-08) comme la session FOURNISSEUR (F-05) sont portées par le COOKIE
// httpOnly posé par l'API (`credentials: 'include'`). Aucune donnée métier en localStorage.
// LECTURE SEULE : aucune mutation → aucun en-tête CSRF (double-submit réservé aux écritures).

import {
  CatalogApiError,
  type CatalogAdminClient,
  type CatalogSupplierClient,
  type CatalogVersionDto,
  type CurrentCatalogVersionDto,
  type SupplierCatalogReferentialDto,
  type TjmEntryTargetDto,
} from '@/api/catalog-client';

/** Forme du corps d'erreur structuré NestJS (`{ statusCode, code?, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly code?: string;
  readonly message?: string;
}

/**
 * Exécute un GET admin/supplier same-origin (cookie httpOnly via `credentials: 'include'`),
 * parse le JSON typé, ou lève une `CatalogApiError` relayant le message serveur.
 * `errorLabel` = domaine (admin/fournisseur) pour un message de repli explicite.
 */
async function getJson<T>(
  baseUrl: string,
  path: string,
  errorLabel: string,
): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'GET',
    // Session (admin ou fournisseur) = cookie httpOnly : transmis avec credentials.
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  });
  if (!response.ok) {
    throw await toCatalogApiError(response, errorLabel);
  }
  return (await response.json()) as T;
}

/** Convertit une réponse d'erreur en `CatalogApiError` (message serveur, sinon repli). */
async function toCatalogApiError(
  response: Response,
  errorLabel: string,
): Promise<CatalogApiError> {
  let body: StructuredErrorBody | undefined;
  try {
    body = (await response.json()) as StructuredErrorBody;
  } catch {
    body = undefined;
  }
  const message =
    body?.message ??
    `Erreur API (${String(response.status)}) sur le catalogue (${errorLabel}).`;
  return new CatalogApiError(message, undefined, response.status);
}

/**
 * Adaptateur HTTP réel du browser des versions ADMIN (A-08). `baseUrl` par défaut vide
 * (chemins relatifs : SPA et API partagent l'origine, ou proxy). En test : fetch mocké.
 */
export class HttpCatalogAdminClient implements CatalogAdminClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async listVersions(): Promise<readonly CatalogVersionDto[]> {
    return getJson<CatalogVersionDto[]>(
      this.baseUrl,
      '/admin/catalog/versions',
      'admin',
    );
  }
}

/**
 * Adaptateur HTTP réel de la version publiée courante FOURNISSEUR (F-05). Le back renvoie
 * `null` (200, corps `null`) si aucune version n'est publiée — `getCurrentVersion` tolère
 * ce corps `null` (pas une erreur : simplement « aucune version courante »).
 */
export class HttpCatalogSupplierClient implements CatalogSupplierClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async getCurrentVersion(): Promise<CurrentCatalogVersionDto | null> {
    return getJson<CurrentCatalogVersionDto | null>(
      this.baseUrl,
      '/supplier/catalog/current',
      'fournisseur',
    );
  }

  public async listCurrentReferential(): Promise<SupplierCatalogReferentialDto> {
    return getJson<SupplierCatalogReferentialDto>(
      this.baseUrl,
      '/supplier/catalog/current/profiles',
      'fournisseur',
    );
  }

  public async resolveTjmEntryTarget(
    profileCode: string,
  ): Promise<TjmEntryTargetDto | null> {
    // Le back renvoie `null` (200) si aucune cible : `getJson` tolère ce corps null (pas une erreur).
    return getJson<TjmEntryTargetDto | null>(
      this.baseUrl,
      `/supplier/catalog/tjm-entry-target/${encodeURIComponent(profileCode)}`,
      'fournisseur',
    );
  }
}
